/**
 * Fetchers for the League Detail screen. One fetcher per endpoint; pure helpers.
 */

import axios from 'axios';
import { API_ENDPOINTS, buildApiUrl } from '../../../constants/api';
import type {
  RawLeagueAnalytics,
  RawLeagueStandingsPayload,
} from '../types/leagueDetail.types';

interface ApiResponse<T> {
  success?: boolean;
  data?: T;
}

export async function fetchLeagueStandings(
  leagueId: string
): Promise<RawLeagueStandingsPayload | null> {
  try {
    const url = buildApiUrl(API_ENDPOINTS.leagues.byIdStandings(leagueId));
    const response = await axios.get<ApiResponse<RawLeagueStandingsPayload>>(url);
    if (!response.data?.success || !response.data.data) return null;
    return response.data.data;
  } catch {
    return null;
  }
}

export async function fetchLeagueAnalytics(
  leagueId: string
): Promise<RawLeagueAnalytics | null> {
  try {
    const url = buildApiUrl(API_ENDPOINTS.leagues.byIdAnalytics(leagueId));
    const response = await axios.get<ApiResponse<RawLeagueAnalytics | null>>(url);
    if (!response.data?.success) return null;
    return response.data.data ?? null;
  } catch {
    return null;
  }
}

/** One page of raw schedule rows plus whether more pages exist. */
export interface LeagueSchedulePage {
  matches: unknown[];
  hasMore: boolean;
}

interface ScheduleApiResponse {
  success?: boolean;
  data?: unknown[];
  hasMore?: boolean;
}

/** Raw match rows (Prisma shape) from /leagues/:id/schedule. Loose typed; callers normalize. */
export async function fetchLeagueSchedule(
  leagueId: string,
  page = 0,
  limit = 20
): Promise<LeagueSchedulePage> {
  try {
    const url = `${buildApiUrl(
      API_ENDPOINTS.leagues.byIdSchedule(leagueId)
    )}?limit=${limit}&page=${page}`;
    const response = await axios.get<ScheduleApiResponse>(url);
    if (!response.data?.success || !Array.isArray(response.data.data)) {
      return { matches: [], hasMore: false };
    }
    return { matches: response.data.data, hasMore: Boolean(response.data.hasMore) };
  } catch {
    return { matches: [], hasMore: false };
  }
}
